import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, TextField, Accordion, AccordionSummary, AccordionDetails, Fab, Zoom } from '@mui/material';
import { ExpandMore, KeyboardArrowUp, Phone as PhoneIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { WHATSAPP } from '../theme/tokens';
import { SUPPORT_PHONE_DISPLAY, supportCallHref, supportWhatsAppHref } from '../utils/support';
import AuthPage from './AuthPage';
import VendoraLogo from './VendoraLogo';
import TermsAndConditionsDialog from './TermsAndConditionsDialog';

const HEADING = "'Plus Jakarta Sans', sans-serif";

// Drop images into frontend_v2/public/categories/ with these filenames.
// Until an image exists (or if it fails to load) the emoji shows as a fallback.
const CATEGORIES = [
    { label: 'Milk', file: 'milk.jpg', emoji: '🥛', tint: '#FDF0E9' },
    { label: 'Newspaper', file: 'newspaper.jpg', emoji: '📰', tint: '#EEF3FE' },
    { label: 'Biryani', file: 'biryani.jpg', emoji: '🍛', tint: '#FBEAEA' },
    { label: 'Oil', file: 'oil.jpg', emoji: '🫗', tint: '#EAF7EF' },
];

const FAQS = [
    {
        q: 'What can I order?',
        a: 'Milk, fresh fruits and vegetables, meat, home-cooked meals, bakery items and groceries — from the vendors already serving your building.',
    },
    {
        q: 'How do vendors sign up?',
        a: 'Enter your phone number and verify with a one-time code. First-time vendors get an account automatically; returning vendors sign in with a password or OTP.',
    },
    {
        q: 'How does billing work?',
        a: 'Recurring and one-off orders roll into a single monthly bill. Invoices go out on WhatsApp and dues are tracked automatically.',
    },
    {
        q: 'Who operates Vendora88?',
        a: 'Vendora88 is a product of Yolotech88 Private Limited, which builds and operates the platform.',
    },
];

export default function LandingPage({ onLoginSuccess }) {
    // Customers arriving from a shared bill link (?view=customer or ?mobile=) go
    // straight to login/sign-up instead of the marketing page.
    const customerLoginIntent = (() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('view') === 'customer' || !!params.get('mobile');
    })();
    const [showAuth, setShowAuth] = useState(customerLoginIntent);
    const [authMobile, setAuthMobile] = useState('');
    const [phone, setPhone] = useState('');
    const [termsOpen, setTermsOpen] = useState(false);
    // Show the back-to-top button the moment the vendor sign-in card leaves the
    // viewport (i.e. logging in is no longer possible without scrolling up).
    const [showScrollTop, setShowScrollTop] = useState(false);
    const signInRef = useRef(null);
    // Floating Call / WhatsApp buttons step aside while the footer's own Contact us block is on
    // screen (two of the same buttons at once), and while the phone box is being typed in (the
    // keyboard would push them up over the field).
    const [footerContactVisible, setFooterContactVisible] = useState(false);
    const [phoneFocused, setPhoneFocused] = useState(false);
    const footerContactRef = useRef(null);
    const continueRef = useRef(null);

    // While the phone box is being typed in, keep Continue above the keyboard. The browser only
    // scrolls far enough to show the box itself, so on smaller phones (Pixel 7) the button sat
    // under the keyboard. The keyboard slides up over ~300ms and the visible area shrinks as it
    // does, so follow the visual viewport rather than guessing one delay.
    useEffect(() => {
        if (!phoneFocused) return undefined;
        const showContinue = () => continueRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        const timer = setTimeout(showContinue, 350);
        const vv = window.visualViewport;
        vv?.addEventListener('resize', showContinue);
        return () => { clearTimeout(timer); vv?.removeEventListener('resize', showContinue); };
    }, [phoneFocused]);

    useEffect(() => {
        const el = footerContactRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            ([entry]) => setFooterContactVisible(entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [showAuth]);

    useEffect(() => {
        const el = signInRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            ([entry]) => setShowScrollTop(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [showAuth]);

    if (showAuth) {
        return <AuthPage onLoginSuccess={onLoginSuccess} initialMobile={authMobile} />;
    }

    const goToDiscover = () => { window.location.href = '/discover'; };

    const handleVendorContinue = () => {
        setAuthMobile(phone);
        setShowAuth(true);
    };

    const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '.03em' };
    const cardTitle = { fontFamily: HEADING, fontWeight: 700, fontSize: 16, color: '#0E1729' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fff', maxWidth: 480, mx: 'auto', fontFamily: "'Manrope', sans-serif" }}>
            {/* Sticky header */}
            <Box sx={{
                position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: '9px',
                px: '18px', pb: '12px', pt: 'calc(12px + env(safe-area-inset-top, 0px))',
                bgcolor: '#fff', borderBottom: '1px solid #EDF1F6',
            }}>
                <VendoraLogo size={32} />
                <Typography sx={{ fontFamily: HEADING, fontWeight: 800, fontSize: 18, color: '#0E1729' }}>
                    Vendora88
                </Typography>
            </Box>

            {/* Hero */}
            <Box sx={{ px: '18px', pt: '18px', pb: '6px' }}>
                <Typography component="h1" sx={{ fontFamily: HEADING, fontWeight: 800, color: '#0E1729', fontSize: 23, lineHeight: 1.12, letterSpacing: '-.02em', mb: '5px' }}>
                    Daily essentials, right from your gate.
                </Typography>
                <Typography sx={{ color: '#556173', fontSize: 14, lineHeight: 1.5 }}>
                    Order from trusted local vendors — or run your own delivery rounds.
                </Typography>
            </Box>

            {/* Two path cards */}
            <Box sx={{ px: '18px', pt: '12px', pb: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Resident (customer = orange) */}
                <Box sx={{ background: 'linear-gradient(150deg,#FDF1E5,#FFFFFF)', border: '1px solid #F6DCC0', borderRadius: '16px', p: '16px 18px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'secondary.main', flex: 'none' }} />
                        <Box>
                            <Box sx={{ ...eyebrow, color: 'secondary.dark' }}>I LIVE HERE</Box>
                            <Box sx={cardTitle}>Order from my gate</Box>
                        </Box>
                    </Box>
                    <Button fullWidth onClick={goToDiscover} disableElevation sx={{
                        bgcolor: 'secondary.main', color: '#fff', fontWeight: 700, fontSize: 14, py: '13px', borderRadius: '11px',
                        '&:hover': { bgcolor: '#cf680a' },
                    }}>
                        Find vendors near me →
                    </Button>
                </Box>

                {/* Vendor (vendor = blue) with inline phone sign-in */}
                <Box ref={signInRef} sx={{ background: 'linear-gradient(150deg,#EEF3FE,#FFFFFF)', border: '1px solid #DCE7FB', borderRadius: '16px', p: '16px 18px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'primary.main', flex: 'none' }} />
                        <Box>
                            <Box sx={{ ...eyebrow, color: 'primary.main' }}>I SELL &amp; DELIVER</Box>
                            <Box sx={cardTitle}>Sign in to run my business</Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px', mb: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', border: '1.5px solid #E4E9F0', bgcolor: '#fff', borderRadius: '11px', px: '12px', fontWeight: 700, color: '#0E1729', fontSize: 15 }}>
                            +91
                        </Box>
                        <TextField
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && phone.length === 10) handleVendorContinue(); }}
                            onFocus={() => setPhoneFocused(true)}
                            onBlur={() => setPhoneFocused(false)}
                            placeholder="98765 43210"
                            type="tel"
                            // enterKeyHint: the keypad's action key reads "Go" and submits, so the
                            // number can be sent without reaching for the button at all.
                            inputProps={{ inputMode: 'numeric', maxLength: 10, enterKeyHint: 'go' }}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '11px', bgcolor: '#fff', fontSize: 16, fontWeight: 600, color: '#0E1729',
                                    '& fieldset': { borderColor: 'primary.main', borderWidth: '1.5px' },
                                    '&:hover fieldset': { borderColor: 'primary.main' },
                                    '&.Mui-focused fieldset': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,.16)' },
                                },
                                '& .MuiOutlinedInput-input': { padding: '13px 14px' },
                            }}
                        />
                    </Box>
                    <Button ref={continueRef} fullWidth onClick={handleVendorContinue} disabled={phone.length !== 10} disableElevation sx={{
                        // a little room below it when scrolled into view above the keyboard
                        scrollMarginBottom: '16px',
                        bgcolor: 'primary.main', color: '#fff', fontWeight: 700, fontSize: 14, py: '13px', borderRadius: '11px',
                        '&:hover': { bgcolor: '#1f56cf' },
                        '&.Mui-disabled': { bgcolor: '#9db8f2', color: '#fff' },
                    }}>
                        Continue →
                    </Button>
                    <Typography sx={{ textAlign: 'center', color: '#8A94A6', fontSize: 11, lineHeight: 1.45, mt: '9px' }}>
                        We'll text a one-time code. New vendors are signed up automatically.
                    </Typography>
                </Box>
            </Box>

            {/* Categories */}
            <Box sx={{ pt: '14px', pb: '22px' }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, color: '#8A94A6', m: '0 18px 12px' }}>
                    Popular near you
                </Typography>
                <Box sx={{ display: 'flex', gap: '10px', overflowX: 'auto', px: '18px', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                    {CATEGORIES.map(({ label, file, emoji, tint }) => (
                        <Box key={label} onClick={goToDiscover} sx={{ flex: 'none', width: 74, textAlign: 'center', cursor: 'pointer' }}>
                            <Box sx={{ position: 'relative', width: 74, height: 58, borderRadius: '14px', bgcolor: tint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.9rem', overflow: 'hidden' }}>
                                {emoji}
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/categories/${file}`}
                                    alt={label}
                                    loading="lazy"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0E1729', mt: '6px' }}>{label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* About */}
            <Box sx={{ p: '24px 18px', bgcolor: '#F5F8FC', borderTop: '1px solid #EDF1F6' }}>
                <Box component="span" sx={{ display: 'inline-block', bgcolor: '#EEF3FE', color: 'primary.main', fontWeight: 700, fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', p: '5px 10px', borderRadius: '999px', mb: '12px' }}>
                    About Vendora88
                </Box>
                <Typography component="h2" sx={{ fontFamily: HEADING, fontWeight: 800, color: '#0E1729', fontSize: 20, letterSpacing: '-.02em', mb: '10px' }}>
                    What is Vendora88?
                </Typography>
                <Typography sx={{ color: '#556173', fontSize: 14, lineHeight: 1.65, mb: '14px' }}>
                    Vendora88 is a neighbourhood marketplace that connects local vendors — milk, fruits, meat, cooked food and daily needs — with residents of gated communities across Indian metros.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E4E9F0', borderRadius: '12px', p: '14px' }}>
                        <Typography sx={{ fontFamily: HEADING, fontWeight: 700, color: 'secondary.dark', fontSize: 13, mb: '4px' }}>For residents</Typography>
                        <Typography sx={{ color: '#556173', fontSize: 13, lineHeight: 1.5 }}>Order daily essentials from vendors at your gate. One monthly bill, invoices on WhatsApp.</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E4E9F0', borderRadius: '12px', p: '14px' }}>
                        <Typography sx={{ fontFamily: HEADING, fontWeight: 700, color: 'primary.main', fontSize: 13, mb: '4px' }}>For vendors</Typography>
                        <Typography sx={{ color: '#556173', fontSize: 13, lineHeight: 1.5 }}>Run daily rounds, monthly billing and dues tracking — all from your phone.</Typography>
                    </Box>
                </Box>
            </Box>

            {/* FAQ */}
            <Box sx={{ p: '24px 18px' }}>
                <Typography component="h2" sx={{ fontFamily: HEADING, fontWeight: 800, color: '#0E1729', fontSize: 20, letterSpacing: '-.02em', mb: '14px' }}>
                    FAQs
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                    {FAQS.map(({ q, a }, i) => (
                        <Accordion
                            key={q}
                            defaultExpanded={i === 0}
                            disableGutters
                            elevation={0}
                            sx={{
                                bgcolor: '#fff', border: '1px solid #E4E9F0', borderRadius: '12px !important',
                                '&:before': { display: 'none' }, px: '15px',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore sx={{ color: 'primary.main', fontSize: 20 }} />}
                                sx={{ p: 0, minHeight: 'unset', '& .MuiAccordionSummary-content': { m: '14px 0' } }}
                            >
                                <Typography sx={{ fontFamily: HEADING, fontWeight: 700, fontSize: 14, color: '#0E1729' }}>{q}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: '0 0 14px' }}>
                                <Typography sx={{ color: '#556173', fontSize: 13, lineHeight: 1.6 }}>
                                    {q === 'Who operates Vendora88?' ? (
                                        <>Vendora88 is a product of <strong style={{ color: '#0E1729' }}>Yolotech88 Private Limited</strong>, which builds and operates the platform.</>
                                    ) : a}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: '#0E1729', color: '#fff', pt: '26px', px: '18px', pb: 'calc(22px + env(safe-area-inset-bottom, 0px))' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '10px' }}>
                    <VendoraLogo size={28} />
                    <Typography sx={{ fontFamily: HEADING, fontWeight: 800, fontSize: 16, color: '#fff' }}>Vendora88</Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,.6)', fontSize: 13, lineHeight: 1.55, mb: '14px' }}>
                    The neighbourhood marketplace for gated communities across Indian metros.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', fontSize: 13, color: 'rgba(255,255,255,.65)', mb: '16px' }}>
                    <Box component="span" onClick={goToDiscover} sx={{ cursor: 'pointer' }}>For residents</Box>
                    <Box component="span" onClick={() => setShowAuth(true)} sx={{ cursor: 'pointer' }}>For vendors</Box>
                    <Box component="span" onClick={() => setTermsOpen(true)} sx={{ cursor: 'pointer' }}>Terms</Box>
                    <Box component="span" onClick={() => { window.location.href = '/privacy'; }} sx={{ cursor: 'pointer' }}>Privacy</Box>
                </Box>
                {/* Contact us — a person to ring or message before signing up */}
                <Box ref={footerContactRef} sx={{ mb: '14px' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,.6)', fontSize: 12, fontWeight: 700, letterSpacing: '.03em', mb: '4px' }}>
                        CONTACT US
                    </Typography>
                    <Typography component="a" href={supportCallHref}
                        sx={{ display: 'inline-block', color: '#fff', fontFamily: HEADING, fontWeight: 800, fontSize: 20, textDecoration: 'none', mb: '10px' }}>
                        &#x1F4DE; {SUPPORT_PHONE_DISPLAY}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '10px' }}>
                        <Button component="a" href={supportCallHref} variant="contained" startIcon={<PhoneIcon />}
                            sx={{ flex: 1, minHeight: 44, textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                                  bgcolor: 'primary.main', '&:hover': { bgcolor: '#1f56cf' } }}>
                            Call
                        </Button>
                        <Button component="a" href={supportWhatsAppHref()} target="_blank" rel="noopener noreferrer"
                            variant="contained" startIcon={<WhatsAppIcon />}
                            sx={{ flex: 1, minHeight: 44, textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                                  bgcolor: WHATSAPP.main, '&:hover': { bgcolor: WHATSAPP.hover } }}>
                            WhatsApp
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ bgcolor: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)', p: '10px 13px', borderRadius: '10px', fontSize: 12, color: 'rgba(255,255,255,.85)', lineHeight: 1.4 }}>
                    Vendora88 is a product of <strong style={{ color: '#fff' }}>Yolotech88 Private Limited</strong>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,.45)', fontSize: 11, mt: '14px' }}>
                    © 2026 Yolotech88 Private Limited. All rights reserved.
                </Typography>
            </Box>

            {/* Floating contact: WhatsApp (green) with Call (blue) above it — the pattern people
                already know from other business sites, one tap each. They step aside while the
                footer's own Contact us block is on screen, and while the phone box is being typed in. */}
            <Zoom in={!footerContactVisible && !phoneFocused}>
                <Box sx={{
                    position: 'fixed', zIndex: 20,
                    // Pinned to the page's 480px column, not the window: on a desktop the window
                    // edge is far outside the page. On a phone the column is the screen.
                    right: 'max(24px, calc(50% - 216px))',
                    bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
                }}>
                    <Fab size="medium" component="a" href={supportCallHref} aria-label={`Call ${SUPPORT_PHONE_DISPLAY}`}
                        sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: '#1f56cf' } }}>
                        <PhoneIcon />
                    </Fab>
                    <Fab component="a" href={supportWhatsAppHref()} target="_blank" rel="noopener noreferrer"
                        aria-label="WhatsApp us"
                        sx={{ bgcolor: WHATSAPP.main, color: '#fff', '&:hover': { bgcolor: WHATSAPP.hover } }}>
                        <WhatsAppIcon sx={{ fontSize: 30 }} />
                    </Fab>
                </Box>
            </Zoom>

            {/* Back-to-top: jumps to the vendor sign-in / hero at the top. Bottom-left, so it
                never sits on top of the Call / WhatsApp buttons on the right. */}
            <Zoom in={showScrollTop}>
                <Fab
                    size="medium"
                    aria-label="Back to sign in"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    sx={{
                        position: 'fixed', left: 'max(24px, calc(50% - 216px))', zIndex: 20,
                        bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
                        bgcolor: 'primary.main', color: '#fff',
                        '&:hover': { bgcolor: '#1f56cf' },
                    }}
                >
                    <KeyboardArrowUp />
                </Fab>
            </Zoom>

            <TermsAndConditionsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
        </Box>
    );
}
